import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/webhooks')({
  component: WebhooksPage,
  head: () => ({
    meta: [
      { title: 'Webhooks Explained | Matricks' },
      { name: 'description', content: 'Learn what webhooks are, how they work, and best practices for implementing them. Complete guide to event-driven communication between applications.' },
    ],
  }),
});

const benefits = [
  {
    iconName: "Zap",
    title: "Real-Time Updates",
    description: "Get notified immediately when events occur, instead of constantly checking for changes.",
  },
  {
    iconName: "RefreshCw",
    title: "Reduced Polling",
    description: "Eliminate inefficient polling that wastes resources checking for updates that may not exist.",
  },
  {
    iconName: "Clock",
    title: "Lower Latency",
    description: "Events are pushed as they happen, not discovered on the next polling interval.",
  },
  {
    iconName: "Shield",
    title: "Simple Integration",
    description: "Any application that can receive HTTP requests can consume webhooks—no special libraries needed.",
  },
  {
    iconName: "Code",
    title: "Decoupled Systems",
    description: "Publishers don't need to know about subscribers. Just fire the webhook and move on.",
  },
  {
    iconName: "Webhook",
    title: "Scalable Architecture",
    description: "Event-driven patterns scale better than request-response for many use cases.",
  },
];

const anatomy = [
  { part: "URL Endpoint", description: "The HTTP(S) URL where webhook payloads are sent" },
  { part: "HTTP Method", description: "Usually POST, sometimes PUT—carrying the event data" },
  { part: "Headers", description: "Content-Type (usually JSON), authentication tokens, signatures" },
  { part: "Payload", description: "The event data—what happened, when, and relevant details" },
  { part: "Signature", description: "HMAC signature to verify the webhook came from the expected source" },
];

const faqItems = [
  {
    question: "How are webhooks different from APIs?",
    answer: "With a traditional API, your application makes a request when it needs data (pull). With webhooks, the external service pushes data to your application when events occur (push). Webhooks reverse the direction of communication.",
  },
  {
    question: "How do I secure webhook endpoints?",
    answer: "Key security measures include: verifying webhook signatures using HMAC, using HTTPS only, validating the source IP if possible, implementing rate limiting, and treating webhook data as untrusted (validate and sanitize all inputs).",
  },
  {
    question: "What happens if my endpoint is down?",
    answer: "Well-designed webhook systems implement retry logic with exponential backoff. If your endpoint consistently fails, many services will disable the webhook and notify you. Always implement reliable endpoint infrastructure.",
  },
  {
    question: "Should I process webhooks synchronously?",
    answer: "No—return a 2xx response immediately, then process asynchronously. Webhook senders typically have short timeouts (5-30 seconds). Queue the payload for background processing to avoid timeouts and ensure reliability.",
  },
  {
    question: "How do I handle duplicate webhooks?",
    answer: "Webhook deliveries can be duplicated due to retries. Implement idempotency by tracking event IDs you've processed. Store the event ID before processing, and skip events you've already seen.",
  },
];

function WebhooksPage() {
  return (
    <div>
      <ContentHeader
        title="Webhooks Explained"
        description="Webhooks are HTTP callbacks that deliver real-time notifications when events occur. Instead of polling an API for updates, webhooks push data to your application the moment something happens."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "Webhooks" },
        ]}
        badge="Concept"
      />

      {/* What are Webhooks */}
      <ContentSection id="what-are-webhooks" title="Understanding Webhooks">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Think of webhooks as "reverse APIs." Instead of your application asking "did anything happen?"
            repeatedly, the external service says "something just happened!" when events occur.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            When you register a webhook with a service, you provide a URL that the service will call
            when specific events occur. The service sends an HTTP request to your URL with details
            about what happened—a new order, a payment completed, a message received, etc.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">Polling vs. Webhooks</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
            <div className="rounded-xl border bg-card p-6">
              <h4 className="font-semibold text-red-500">Polling (Inefficient)</h4>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <p>Your app: "Any updates?" → No</p>
                <p>Your app: "Any updates?" → No</p>
                <p>Your app: "Any updates?" → No</p>
                <p>Your app: "Any updates?" → Yes!</p>
                <p className="text-red-500 mt-4">Wasted 3 requests, delayed discovery</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-6">
              <h4 className="font-semibold text-green-500">Webhooks (Efficient)</h4>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <p>Service: [Event occurs]</p>
                <p>Service → Your app: "Here's what happened!"</p>
                <p className="text-green-500 mt-4">Instant notification, no wasted requests</p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        id="benefits"
        title="Key Benefits"
        description="Why webhooks are essential for modern application integration."
        background="muted"
      >
        <FeatureShowcase features={benefits} columns={3} variant="cards" />
      </ContentSection>

      {/* Anatomy */}
      <ContentSection
        id="anatomy"
        title="Anatomy of a Webhook"
        description="Understanding the components of a webhook request."
      >
        <div className="space-y-4">
          {anatomy.map((item) => (
            <div key={item.part} className="flex gap-4 items-start">
              <div className="w-32 flex-shrink-0 font-medium">{item.part}</div>
              <div className="text-muted-foreground">{item.description}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-xl bg-muted/50 p-6">
          <h4 className="font-medium mb-4">Example Webhook Payload</h4>
          <pre className="text-sm overflow-x-auto">
{`POST /webhooks/orders HTTP/1.1
Host: your-app.com
Content-Type: application/json
X-Webhook-Signature: sha256=abc123...

{
  "event": "order.completed",
  "timestamp": "2024-01-15T10:30:00Z",
  "data": {
    "order_id": "ord_12345",
    "customer_email": "customer@example.com",
    "total": 99.99
  }
}`}
          </pre>
        </div>
      </ContentSection>

      {/* Best Practices */}
      <ContentSection
        id="best-practices"
        title="Best Practices"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Respond Quickly</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Return a 2xx status code immediately. Process the webhook asynchronously
              in a background job to avoid timeout issues.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Verify Signatures</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Always validate webhook signatures using HMAC. This prevents attackers
              from sending fake webhooks to your endpoint.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Handle Retries</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Webhooks may be delivered multiple times. Use event IDs and idempotent
              processing to handle duplicates gracefully.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Log Everything</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Log incoming webhooks with their payloads. This helps with debugging
              and provides an audit trail of all events.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/workflow-automation"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Workflow Automation</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Trigger workflows with webhooks
            </p>
          </Link>
          <Link
            to="/concepts/api-gateway"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">API Gateway</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Secure webhook endpoints
            </p>
          </Link>
          <Link
            to="/concepts/data-integration"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Data Integration</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time data sync with webhooks
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Build Webhook-Driven Workflows"
        description="Matricks makes it easy to receive, process, and send webhooks. Create event-driven automations with our visual workflow builder."
        primaryAction={{ label: "Start Building", href: "/login" }}
        secondaryAction={{ label: "View Features", href: "/features" }}
        background="primary"
      />
    </div>
  );
}
